import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { submitApplicationCv, getMyOnboardingStatus } from "@/lib/applications.functions";
import { parseMyCv } from "@/lib/cv-parse.functions";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/bartender/application")({
  head: () => ({
    meta: [
      { title: "Upload Your Experience — The Hospitality Reserve" },
      {
        name: "description",
        content:
          "Upload your CV and we'll build your bartender profile automatically. Minimum 6 months combined bar experience required.",
      },
    ],
  }),
  component: ApplicationPage,
});

type Phase = "idle" | "uploading" | "checking" | "building";

const PHASE_LABEL: Record<Exclude<Phase, "idle">, string> = {
  uploading: "Uploading CV…",
  checking: "AI checking experience requirement…",
  building: "Building profile…",
};

const ACCEPTED_EXT = [
  "pdf", "docx", "doc", "odt", "rtf", "txt", "md", "csv", "html", "htm",
  "png", "jpg", "jpeg", "webp", "gif", "heic", "heif",
];
const MAX_BYTES = 10 * 1024 * 1024;

function ApplicationPage() {
  const navigate = useNavigate();
  const submit = useServerFn(submitApplicationCv);
  const parse = useServerFn(parseMyCv);
  const status = useServerFn(getMyOnboardingStatus);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState<Phase>("idle");
  const [dragging, setDragging] = useState(false);
  const [dob, setDob] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      const s = await status();
      if (s.status === "approved") { navigate({ to: "/bartender", replace: true }); return; }
      if (s.status === "rejected") { navigate({ to: "/bartender/rejected", replace: true }); return; }
      setLoading(false);
    })();
  }, []);

  // Maximum DOB so the user cannot pick a future / under-18 date in the picker.
  const maxDob = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 18);
    return d.toISOString().slice(0, 10);
  })();

  const handleUpload = async (file: File) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const typeOk = ACCEPTED_EXT.includes(ext) || file.type.startsWith("image/");
    if (!typeOk) { toast.error("That file type isn't supported. Try PDF, Word, text or a photo of your CV."); return; }
    if (file.size > MAX_BYTES) { toast.error("CV must be under 10MB."); return; }
    if (!dob) { toast.error("Please enter your date of birth before uploading."); return; }
    if (dob > maxDob) { toast.error("You must be at least 18 years old to serve alcohol in the UK."); return; }

    setPhase("uploading");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      if (!user) throw new Error("Not signed in.");
      const path = `${user.id}/cv.${ext || "pdf"}`;
      const { error } = await supabase.storage.from("cvs").upload(path, file, {
        upsert: true,
        contentType: file.type || "application/octet-stream",
      });
      if (error) throw error;

      const r = await submit({ data: { cv_file_path: path, date_of_birth: dob } });
      if ("error" in r) throw new Error(r.error);

      setPhase("checking");
      const parsed = await parse({ data: { cv_file_path: path } });
      if ("error" in parsed) {
        if (parsed.error === "REVIEW") {
          toast.success("Your CV has been sent to our team for a manual check.");
          navigate({ to: "/application-status", replace: true });
          return;
        }
        throw new Error(parsed.error);
      }

      setPhase("building");
      await new Promise((res) => setTimeout(res, 600));

      toast.success(`${parsed.total_months_experience} months of bar experience verified.`);
      navigate({ to: "/bartender/profile-review", replace: true });

    } catch (err) {
      setPhase("idle");
      toast.error(err instanceof Error ? err.message : "Could not upload.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-dvh bg-background text-foreground flex items-center justify-center">
        <p className="tracking-wide-luxury text-muted-foreground">Loading…</p>
      </div>
    );
  }

  const busy = phase !== "idle";

  return (
    <div className="min-h-dvh bg-background text-foreground flex flex-col">
      <header className="px-6 py-5 border-b border-border">
        <p className="tracking-wide-luxury text-accent text-xs">The Hospitality Reserve</p>
        <p className="font-display text-lg leading-tight">Application</p>
      </header>
      <main className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-md">
          <p className="text-center tracking-wide-luxury text-accent text-xs">One last step</p>
          <span className="block mx-auto mt-5 h-px w-12 bg-accent/60" />
          <h1 className="mt-6 font-display text-3xl text-center leading-tight">Upload Your Experience</h1>
          <p className="mt-4 text-center text-sm text-muted-foreground leading-relaxed">
            We'll read your CV and build your profile automatically — usually in under a minute.
          </p>

          <div className="mt-8 rounded-2xl bg-white text-foreground p-6 shadow-sm">
            {busy ? (
              <div className="py-6">
                <div className="flex items-center gap-3">
                  <span className="h-2.5 w-2.5 rounded-full bg-accent animate-pulse" />
                  <p className="text-sm font-medium">{PHASE_LABEL[phase as Exclude<Phase, "idle">]}</p>
                </div>
                <ol className="mt-5 space-y-3 text-sm">
                  {(["uploading", "checking", "building"] as const).map((p, i) => {
                    const order = ["uploading", "checking", "building"] as const;
                    const done = order.indexOf(phase as (typeof order)[number]) > i;
                    const active = phase === p;
                    return (
                      <li
                        key={p}
                        className={
                          done ? "text-foreground/50 line-through"
                          : active ? "text-foreground font-medium"
                          : "text-foreground/40"
                        }
                      >
                        {PHASE_LABEL[p]}
                      </li>
                    );
                  })}
                </ol>
                <p className="mt-6 text-xs text-foreground/60">Please keep this page open.</p>
              </div>
            ) : (
              <>
                <div className="mb-5 rounded-xl border-l-2 border-accent bg-accent/5 px-4 py-3">
                  <p className="text-xs tracking-wide-luxury text-accent uppercase">Experience requirement</p>
                  <p className="mt-1 text-sm text-foreground leading-relaxed">
                    Minimum 6 months total relevant bar/hospitality experience required (can be combined
                    across multiple venues).
                  </p>
                </div>

                <div className="mb-5 rounded-xl bg-foreground/5 px-4 py-3">
                  <p className="text-sm text-foreground leading-relaxed">
                    Please remove your <strong>home address</strong> and <strong>phone number</strong> from
                    your CV. For your privacy we also automatically strip addresses, postcodes, phone
                    numbers, and emails before venues see it.
                  </p>
                </div>

                <div className="space-y-2">
                  <label htmlFor="dob" className="text-xs tracking-wide-luxury text-foreground/70">
                    Date of birth (required)
                  </label>
                  <input
                    id="dob"
                    type="date"
                    value={dob}
                    max={maxDob}
                    onChange={(e) => setDob(e.target.value)}
                    className="w-full rounded-xl border border-foreground/20 bg-white px-3 py-2 text-sm focus:border-foreground focus:outline-none focus:ring-2 focus:ring-accent/30"
                  />
                  <p className="text-xs text-foreground/60">
                    UK law requires bartenders to be at least 18 to serve alcohol.
                  </p>
                </div>

                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.odt,.rtf,.txt,.md,.csv,.html,image/*"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleUpload(f); }}
                />

                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Upload your CV"
                  onClick={() => fileRef.current?.click()}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileRef.current?.click(); }}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    const f = e.dataTransfer.files?.[0];
                    if (f) void handleUpload(f);
                  }}
                  className={`mt-6 cursor-pointer rounded-2xl border-2 border-dashed px-4 py-10 text-center transition ${
                    dragging ? "border-accent bg-accent/5" : "border-foreground/20 hover:border-foreground/40"
                  }`}
                >
                  <p className="text-sm font-medium">Drag & drop your CV here</p>
                  <p className="mt-1 text-xs text-foreground/60">or tap to choose a file</p>
                  <p className="mt-3 text-xs text-foreground/50">PDF, Word, text — or a photo/scan of your CV · max 10MB</p>
                </div>

                <Button
                  type="button"
                  disabled={!dob}
                  onClick={() => fileRef.current?.click()}
                  className="mt-6 w-full rounded-xl bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury py-6"
                >
                  {dob ? "Choose file & submit" : "Enter date of birth first"}
                </Button>

                <Link
                  to="/dashboard"
                  className="mt-3 block text-center text-xs tracking-wide-luxury text-foreground/60 hover:text-accent py-2"
                >
                  Back to dashboard
                </Link>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
